/**
 * ProposalWizard — `/proposals/{id}/edit` (Phase G4A CP2).
 *
 * The 10-step guided wizard shell. Composition:
 *   - Left column: step index with completion state
 *   - Main pane: step form (rendered by `renderStep` from wizardSteps.jsx)
 *   - Top strip: working-title editor + status pill + auto-save indicator
 *   - Bottom bar: Previous / Save Draft / Next
 *   - Sticky footer: persistent AI-disclosure banner (approved copy)
 *
 * Auto-save discipline (per user CP2 brief):
 *   - Debounced 30-second timer while editing; PATCHes the current
 *     step's slice via `PATCH /api/proposals/{id}` when the local
 *     `dirty` flag is true.
 *   - Every Next / Previous navigation flushes any pending changes
 *     before the step swap.
 *   - Explicit "Save Draft" button flushes immediately.
 *
 * Voice discipline: all copy in this file — including the persistent
 * footer banner — is sourced from a factual, non-hype vocabulary.
 * Approved honest-gate copy for Steps 9-10 lives in wizardSteps.jsx.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Save,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import AppNav from "@/components/app/AppNav";
import AIDisclosureBadge from "@/components/AIDisclosureBadge";
import CoachPanel from "@/components/proposals/CoachPanel";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import { api, formatApiError } from "@/api/client";
import { STEPS, STEP_ICONS, isStepFilled, statusMeta } from "@/components/proposals/utils";
import { renderStep } from "@/components/proposals/wizardSteps";

const AUTO_SAVE_INTERVAL_MS = 30_000;

export default function ProposalWizard() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [proposal, setProposal] = useState(null);
  const [coachContext, setCoachContext] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Locally-edited wizard_data + title. Diffed against the last
  // server-known versions to compute the `dirty` flag.
  const [wizardData, setWizardData] = useState({});
  const [title, setTitle] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const stepNumber = Math.max(
    1,
    Math.min(10, Number(searchParams.get("step")) || 1),
  );
  const activeStep = STEPS[stepNumber - 1];

  const lastSyncedRef = useRef({ wizardData: {}, title: "" });
  const dirtyRef = useRef(false);

  // Keep `dirtyRef` in sync so the auto-save interval callback can
  // read the freshest value without re-registering.
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  // ------------------------------------------------------------------
  // Load
  // ------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setCoachContext(null);
    try {
      const res = await api.get(`/proposals/${id}`);
      setProposal(res.data);
      setWizardData(res.data?.wizard_data || {});
      setTitle(res.data?.title || "");
      lastSyncedRef.current = {
        wizardData: res.data?.wizard_data || {},
        title: res.data?.title || "",
      };
      setDirty(false);
      setSaveState("idle");
    } catch (err) {
      // Discriminate by `code` first. G4D CP2 fix (2026-07-20):
      // `proposals_premium_only` no longer bounces the user out —
      // free-tier callers get to view Step 10 in a "free-tier
      // shell" (locked quality-score placeholder + CoachPanel
      // fully functional). The shell mounts against the
      // purpose-built `/coach-context` endpoint (no premium gate).
      // `feature_disabled` STILL redirects (the module is off).
      const code = err?.response?.data?.detail?.code;
      const status = err?.response?.status;
      if (code === "feature_disabled") {
        navigate("/proposals", { replace: true });
        return;
      }
      if (code === "proposals_premium_only") {
        // Free-tier fallback — fetch the coach-context surface.
        try {
          const ctxRes = await api.get(`/proposals/${id}/coach-context`);
          setCoachContext(ctxRes.data);
          setTitle(ctxRes.data?.title || "");
          // No wizard_data on free-tier — the shell is read-only.
          setWizardData({});
          lastSyncedRef.current = { wizardData: {}, title: ctxRes.data?.title || "" };
          setDirty(false);
          setSaveState("idle");
        } catch (ctxErr) {
          // Both endpoints failed — this is the true failure case.
          // Redirect to /proposals + surface an inline banner.
          const ctxStatus = ctxErr?.response?.status;
          if (ctxStatus === 404) {
            setLoadError("Proposal not found or no longer accessible.");
          } else {
            setLoadError(
              "Unable to load proposal — please contact support.",
            );
          }
        }
        return;
      }
      if (status === 404) {
        setLoadError("Proposal not found or no longer accessible.");
      } else {
        setLoadError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------
  const doSave = useCallback(
    async ({ silent = false } = {}) => {
      if (!id) return false;
      const patch = {};
      const current = { wizardData, title };
      if (
        JSON.stringify(current.wizardData) !==
        JSON.stringify(lastSyncedRef.current.wizardData)
      ) {
        patch.wizard_data = current.wizardData;
      }
      if ((current.title || "").trim() !== (lastSyncedRef.current.title || "").trim()) {
        const trimmed = (current.title || "").trim();
        if (trimmed) patch.title = trimmed;
      }
      if (Object.keys(patch).length === 0) {
        setDirty(false);
        return true;
      }
      setSaveState("saving");
      try {
        const res = await api.patch(`/proposals/${id}`, patch);
        setProposal(res.data);
        setWizardData(res.data?.wizard_data || {});
        setTitle(res.data?.title || "");
        lastSyncedRef.current = {
          wizardData: res.data?.wizard_data || {},
          title: res.data?.title || "",
        };
        setDirty(false);
        setSaveState("saved");
        setLastSavedAt(new Date());
        return true;
      } catch (err) {
        setSaveState("error");
        if (!silent) toast.error(formatApiError(err));
        return false;
      }
    },
    [id, wizardData, title],
  );

  // Auto-save every 30s while dirty. `dirtyRef` lets us re-check the
  // freshest value without tearing down the interval.
  useEffect(() => {
    const iv = setInterval(() => {
      if (dirtyRef.current) doSave({ silent: true });
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [doSave]);

  // ------------------------------------------------------------------
  // Change handlers
  // ------------------------------------------------------------------
  function onStepChange(newSlice) {
    if (!activeStep.key) return; // Steps 9/10 don't persist.
    setWizardData((prev) => ({ ...prev, [activeStep.key]: newSlice }));
    setDirty(true);
    setSaveState("idle");
  }

  function onTitleChange(v) {
    setTitle(v);
    setDirty(true);
    setSaveState("idle");
  }

  async function goToStep(next) {
    const clamped = Math.max(1, Math.min(10, next));
    if (clamped === stepNumber) return;
    if (dirty) await doSave({ silent: true });
    setSearchParams({ step: String(clamped) });
  }

  async function onSaveClicked() {
    const ok = await doSave({ silent: false });
    if (ok) toast.success("Draft saved");
  }

  // ------------------------------------------------------------------
  // Derived UI helpers
  // ------------------------------------------------------------------
  const status = proposal?.status || "draft";
  const meta = statusMeta(status);

  const stepData = useMemo(() => {
    if (!activeStep.key) return null;
    return wizardData?.[activeStep.key] || {};
  }, [wizardData, activeStep.key]);

  const StepIcon = STEP_ICONS[stepNumber];

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <main className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)] pb-32">
      <AppNav />
      <div className="mx-auto max-w-6xl px-5 md:px-8 pt-8">
        <Link
          to="/proposals"
          data-testid="wizard-back-to-library"
          className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Proposal Library
        </Link>

        {loading ? (
          <div
            data-testid="wizard-loading"
            className="mt-10 inline-flex items-center gap-2 text-[var(--ds-color-ink-muted)]"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Loading proposal…
          </div>
        ) : loadError ? (
          <div
            data-testid="wizard-load-error"
            className="mt-10 rounded-[var(--ds-radius-card)] border p-6 text-[13px]"
            style={{
              background: "var(--ds-color-danger-soft)",
              borderColor: "var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
            }}
          >
            {loadError}
          </div>
        ) : coachContext ? (
          // G4D CP2 fix (2026-07-20) — free-tier shell. Rendered
          // when `GET /api/proposals/{id}` returned 402 but the
          // purpose-built `/coach-context` returned 200. Shell
          // shows the locked quality-score placeholder + a fully-
          // functional CoachPanel bound to the coach-context data.
          <FreeTierWizardShell
            proposalId={id}
            coachContext={coachContext}
          />
        ) : (
          <>
            {/* --- Header strip: title + status + save indicator ---- */}
            <header className="mt-6 flex items-start justify-between gap-6 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--ds-color-ink-subtle)]">
                  Grant Writer · Working proposal
                </p>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <input
                    data-testid="wizard-title-input"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    onBlur={() => dirty && doSave({ silent: true })}
                    maxLength={200}
                    className="text-[16px] font-semibold md:text-[20px] font-semibold font-semibold tracking-tight text-[var(--ds-color-ink)] bg-transparent border-b border-transparent focus:border-[var(--ds-color-accent)]/50 focus:outline-none w-full max-w-2xl px-1 -mx-1"
                    placeholder="Working title"
                  />
                </div>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <span
                    data-testid="wizard-status-pill"
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em]"
                    style={{
                      background: meta.tintBg,
                      border: `1px solid ${meta.tintBorder}`,
                      color: meta.tint,
                    }}
                  >
                    {meta.label}
                  </span>
                  <AutoSaveIndicator saveState={saveState} lastSavedAt={lastSavedAt} dirty={dirty} />
                </div>
              </div>
              <button
                type="button"
                data-testid="wizard-save-draft-btn"
                onClick={onSaveClicked}
                disabled={saveState === "saving"}
                className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold text-[var(--ds-color-ink)] bg-[var(--ds-color-surface-muted)] hover:bg-[var(--ds-color-surface)] border border-[var(--ds-color-border)] transition-colors disabled:opacity-60"
              >
                {saveState === "saving" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save draft
              </button>
            </header>

            {/* --- Body: sidebar + main pane ---- */}
            <div className="mt-8 grid grid-cols-12 gap-6">
              <aside
                data-testid="wizard-sidebar"
                className="col-span-12 md:col-span-4 lg:col-span-3"
              >
                <ol className="flex flex-col gap-1">
                  {STEPS.map((s) => {
                    const active = s.number === stepNumber;
                    const filled = s.key ? isStepFilled(s.key, wizardData) : false;
                    const Icon = STEP_ICONS[s.number];
                    return (
                      <li key={s.number}>
                        <button
                          type="button"
                          data-testid={`wizard-sidebar-step-${s.number}`}
                          onClick={() => goToStep(s.number)}
                          className={`w-full text-left rounded-[var(--ds-radius-card)] px-3 py-2.5 flex items-center gap-3 transition-colors ${
                            active
                              ? "bg-[var(--ds-color-accent-soft)] border border-[var(--ds-color-accent)]/30"
                              : "bg-transparent border border-transparent hover:bg-[var(--ds-color-surface-muted)]"
                          }`}
                        >
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold uppercase tracking-[0.14em] font-semibold flex-shrink-0"
                            style={
                              active
                                ? {
                                    background: "var(--ds-color-accent-soft)",
                                    color: "rgb(167,139,250)",
                                    border: "1px solid var(--ds-color-accent-soft)",
                                  }
                                : filled
                                ? {
                                    background: "var(--ds-color-success-soft)",
                                    color: "rgb(52,211,153)",
                                    border: "1px solid var(--ds-color-success-soft)",
                                  }
                                : {
                                    background: "var(--ds-color-surface-muted)",
                                    color: "var(--ds-color-ink-muted)",
                                    border: "1px solid var(--ds-color-border)",
                                  }
                            }
                          >
                            {filled ? <Check className="w-4 h-4" /> : s.number}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              data-testid={`wizard-sidebar-step-${s.number}-title`}
                              className={`text-[13px] font-medium truncate ${
                                active ? "text-[var(--ds-color-ink)]" : "text-[var(--ds-color-ink-muted)]"
                              }`}
                            >
                              {s.title}
                            </p>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] truncate">
                              {s.kicker}
                            </p>
                          </div>
                          {s.locked && (
                            <span
                              data-testid={`wizard-sidebar-step-${s.number}-locked`}
                              className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]"
                            >
                              Preview
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </aside>

              <section className="col-span-12 md:col-span-8 lg:col-span-9">
                <div className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface)] p-6 md:p-8">
                  <div className="flex items-start gap-3 flex-wrap">
                    {StepIcon && (
                      <div
                        className="rounded-full p-2.5 flex-shrink-0"
                        style={{
                          background: "var(--ds-color-accent-soft)",
                          border: "1px solid var(--ds-color-accent-soft)",
                        }}
                      >
                        <StepIcon className="w-4 h-4 text-[var(--ds-color-accent)]" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--ds-color-ink-subtle)]">
                        Step {stepNumber} of 10 · {activeStep.kicker}
                      </p>
                      <h2
                        data-testid="wizard-step-title"
                        className="mt-1 text-[16px] font-semibold md:text-[20px] font-semibold font-semibold text-[var(--ds-color-ink)]"
                      >
                        {activeStep.title}
                      </h2>
                    </div>
                    {activeStep.key && <AIDisclosureBadge inline />}
                  </div>

                  <div className="mt-6">
                    {renderStep({
                      stepNumber,
                      stepData,
                      wizardDataFull: wizardData,
                      onChange: onStepChange,
                      proposalId: id,
                    })}
                  </div>

                  {/* --- Bottom bar: Prev / Save / Next ---- */}
                  <div className="mt-8 flex items-center justify-between gap-4 border-t border-[var(--ds-color-border)] pt-6">
                    <button
                      type="button"
                      data-testid="wizard-prev-btn"
                      disabled={stepNumber <= 1}
                      onClick={() => goToStep(stepNumber - 1)}
                      className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] bg-transparent hover:bg-[var(--ds-color-surface-muted)] border border-[var(--ds-color-border)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] hidden md:block">
                      Auto-saves every 30 seconds
                    </div>
                    <button
                      type="button"
                      data-testid="wizard-next-btn"
                      disabled={stepNumber >= 10}
                      onClick={() => goToStep(stepNumber + 1)}
                      className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold text-[var(--ds-color-accent-ink)] bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </>
        )}
      </div>

      <PersistentDisclosureFooter />
    </main>
  );
}

// -----------------------------------------------------------------------
// AutoSaveIndicator — small inline label reflecting the current
// save-state. Shows "Saved just now" for a brief window after a
// successful save, then reverts to a neutral "Draft saved" line.
// -----------------------------------------------------------------------

function AutoSaveIndicator({ saveState, lastSavedAt, dirty }) {
  const [rel, setRel] = useState("");
  useEffect(() => {
    if (!lastSavedAt) {
      setRel("");
      return;
    }
    const update = () => {
      const seconds = Math.max(
        0,
        Math.round((Date.now() - lastSavedAt.getTime()) / 1000),
      );
      if (seconds < 5) setRel("Saved just now");
      else if (seconds < 60) setRel(`Saved ${seconds}s ago`);
      else if (seconds < 3600) setRel(`Saved ${Math.round(seconds / 60)}m ago`);
      else setRel(`Saved ${Math.round(seconds / 3600)}h ago`);
    };
    update();
    const iv = setInterval(update, 5000);
    return () => clearInterval(iv);
  }, [lastSavedAt]);

  if (saveState === "saving") {
    return (
      <span
        data-testid="wizard-autosave-status"
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (saveState === "error") {
    return (
      <span
        data-testid="wizard-autosave-status"
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-red-400"
      >
        Auto-save failed — click Save draft
      </span>
    );
  }
  if (dirty) {
    return (
      <span
        data-testid="wizard-autosave-status"
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]"
      >
        Unsaved changes
      </span>
    );
  }
  if (rel) {
    return (
      <span
        data-testid="wizard-autosave-status"
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]"
      >
        <Check className="w-3 h-3 text-emerald-400" />
        {rel}
      </span>
    );
  }
  return null;
}


// -----------------------------------------------------------------------
// PersistentDisclosureFooter — sticky compliance banner at the
// bottom of the wizard. Non-dismissible. Uses factual language:
// "AI-assisted draft. Review carefully before submitting to any
// grant sponsor. Informed OS does not guarantee eligibility, awards,
// or funding decisions."
// -----------------------------------------------------------------------

function PersistentDisclosureFooter() {
  return (
    <footer
      data-testid="wizard-disclosure-footer"
      className="fixed bottom-0 inset-x-0 z-20 border-t border-[var(--ds-color-border)] bg-[var(--ds-color-background)]/95 backdrop-blur px-4 md:px-8 py-3"
    >
      <div className="mx-auto max-w-6xl flex items-start gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        <ShieldAlert className="w-4 h-4 flex-shrink-0 text-[var(--ds-color-accent)] mt-0.5" />
        <p className="leading-relaxed">
          <strong className="text-[var(--ds-color-ink)]">AI-assisted draft.</strong> Review
          carefully before submitting to any grant sponsor. Informed OS does
          not guarantee eligibility, awards, or funding decisions.
        </p>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// FreeTierWizardShell (G4D CP2 fix, 2026-07-20).
//
// Rendered when a Free-tier caller hits `/proposals/{id}/edit` and
// the full-proposal fetch 402s. Composed of:
//   - Header + working title (read-only)
//   - Locked quality-score placeholder (PremiumUpgradePanel scope="quality_score")
//   - Fully-functional CoachPanel (bound to `/coach-context` data,
//     free-tier 3-message cap enforced by the backend)
//   - No weak-sections / recommended-improvements / export dropdown
//
// The shell is deliberately minimal — the whole point of the fix is
// to keep the premium proposal contract clean while still surfacing
// the Coach "taster" experience to free-tier users.
// ---------------------------------------------------------------------------
function FreeTierWizardShell({ proposalId, coachContext }) {
  const title = coachContext?.title || "Untitled proposal";
  return (
    <div data-testid="wizard-free-tier-shell" className="mt-6 flex flex-col gap-6">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--ds-color-ink-subtle)]">
            Grant Writer · Free preview
          </p>
          <h1
            data-testid="wizard-free-title"
            className="mt-2 text-[20px] font-semibold md:text-[22px] tracking-tight text-[var(--ds-color-ink)]"
          >
            {title}
          </h1>
          <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] leading-relaxed max-w-2xl">
            Full quality analysis and section editing are Premium
            features. You can still use the AI Grant Coach on this
            proposal — free tier includes 3 Coach messages per
            proposal.
          </p>
        </div>
      </header>

      <PremiumUpgradePanel scope="quality_score" />

      <CoachPanel
        proposalId={proposalId}
        coachContext={coachContext}
        wizardBackTo="/proposals"
        testIdPrefix="coach-panel"
      />
    </div>
  );
}
